/**
 * The write layer.
 *
 * Optimism is off by default (§8.1). It is opted into only where the server cannot refuse — a
 * review change's include checkbox — and every write the server validates shows a pending state
 * and waits. `If-Match` travels on every write to a versioned resource, and a 409 comes back
 * carrying the current representation so the screen can show what changed underneath.
 */

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { keys } from '../keys';
import { routes } from '~/lib/routes';
import type * as T from '../schema';

/**
 * A lifecycle edit regenerates both rule chains, so the register and every matter read under it
 * go at once. The coupling is documented in `pages.md` §3.1 and belongs in one place.
 */
function invalidateRegister(client: QueryClient, code: string): Promise<void> {
  return Promise.all([
    client.invalidateQueries({ queryKey: keys.jurisdictionAll(code) }),
    client.invalidateQueries({ queryKey: keys.matterAll(code) }),
    client.invalidateQueries({ queryKey: ['jurisdictions'] }),
    client.invalidateQueries({ queryKey: keys.successions(code) }),
  ]).then(() => undefined);
}

export function useCreateJurisdiction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: T.CreateJurisdictionRequest) => {
      const result = await apiFetch<T.JurisdictionDetail>(routes.api.jurisdictions(), {
        method: 'POST',
        body,
      });
      return result.data;
    },
    onSuccess: (created) => invalidateRegister(client, created.code),
  });
}

/**
 * Record what Manage calls a register, which is also what an import has to match.
 *
 * An opened register an import still cannot place is half a decision: the engine holds `BX` and
 * the file says `BENELUX`, and nothing in between is inferred because "MOROCCO (EX-TANGIER)" and
 * "MOROCCO" are two registers ([ADR-0022]). So opening one from the catalogue records the name
 * it was skipped under, in the same breath.
 */
export function useRecordMapping() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { code: string; matter: string; manageCode: string }) => {
      const result = await apiFetch<T.MappingReport>(
        routes.api.mapping(body.code, body.matter),
        { method: 'PUT', body: { manageCode: body.manageCode } },
      );
      return result.data;
    },
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: keys.mappings() }),
        client.invalidateQueries({ queryKey: ['rule-catalogue'] }),
      ]).then(() => undefined),
  });
}

export function useAddLifecycleEvent(code: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: T.CreateLifecycleEventRequest; etag: string }) => {
      const result = await apiFetch<T.JurisdictionWrite>(routes.api.lifecycleEvents(code), {
        method: 'POST',
        body: input.body,
        etag: input.etag,
      });
      return result.data;
    },
    onSuccess: () => invalidateRegister(client, code),
  });
}

export function useUpdateLifecycleEvent(code: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      body: T.UpdateLifecycleEventRequest;
      etag: string;
    }) => {
      const result = await apiFetch<T.JurisdictionWrite>(
        routes.api.lifecycleEvent(code, input.id),
        { method: 'PATCH', body: input.body, etag: input.etag },
      );
      return result.data;
    },
    onSuccess: () => invalidateRegister(client, code),
  });
}

export function useDeleteLifecycleEvent(code: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; etag: string }) => {
      const result = await apiFetch<T.JurisdictionWrite>(
        routes.api.lifecycleEvent(code, input.id),
        { method: 'DELETE', etag: input.etag },
      );
      return result.data;
    },
    onSuccess: () => invalidateRegister(client, code),
  });
}

export function useAddSuccession(code: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: T.CreateSuccessionRequest) => {
      const result = await apiFetch<T.Successions>(routes.api.successions(code), {
        method: 'POST',
        body,
      });
      return result.data;
    },
    onSuccess: () => invalidateRegister(client, code),
  });
}

export function useRemoveSuccession(code: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await apiFetch<T.Successions>(routes.api.succession(code, id), {
        method: 'DELETE',
      });
      return result.data;
    },
    onSuccess: () => invalidateRegister(client, code),
  });
}

export function useUpdateMatter(code: string, matter: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: T.UpdateMatterRequest) => {
      const result = await apiFetch<T.MatterPanel>(routes.api.matter(code, matter), {
        method: 'PATCH',
        body,
      });
      return result.data;
    },
    onSuccess: () => invalidateRegister(client, code),
  });
}

export function useSaveRule(code: string, matter: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { lane: string; body: T.UpsertRuleRequest; etag?: string | null }) => {
      const result = await apiFetch<T.RuleWrite | T.PendingChange>(
        routes.api.rule(code, matter, input.lane),
        {
          method: 'PUT',
          body: input.body,
          // A rule that does not exist yet has nothing to be stale against.
          etag: input.etag ?? '*',
        },
      );
      // 202 under four-eyes: accepted and held, not written. The caller has to say which,
      // because "saved" for a change that has not been applied is the one wrong thing to say.
      return { written: result.data, held: result.status === 202 };
    },
    onSuccess: () => invalidateRegister(client, code),
  });
}

export function useDeleteRule(code: string, matter: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { lane: string; etag: string }) => {
      const result = await apiFetch<T.MatterPanel | T.PendingChange>(
        routes.api.rule(code, matter, input.lane),
        { method: 'DELETE', etag: input.etag },
      );
      return { removed: result.data, held: result.status === 202 };
    },
    onSuccess: () => invalidateRegister(client, code),
  });
}

/**
 * Upload a corpus. **Writes nothing** — this produces a report and, on commit, a proposal
 * somebody reviews (ADR-0017).
 */
export function useImportCorpus() {
  return useMutation({
    mutationFn: async (input: { format: string; filename: string; body: ArrayBuffer }) => {
      const result = await apiFetch<T.ImportReport>(
        `${routes.api.imports()}?format=${encodeURIComponent(input.format)}`,
        {
          method: 'POST',
          raw: input.body,
          headers: { 'Content-Type': 'text/csv', 'X-Filename': input.filename },
        },
      );
      return result.data;
    },
  });
}

/**
 * Turn a read corpus into a proposal. **Still writes nothing to the register** — it produces the
 * same reviewable object a research run produces, decided through the same review by a person
 * (ADR-0017). Refused twice on one import rather than producing a second proposal for one file.
 */
export function useCommitImport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const result = await apiFetch<T.ImportReport>(routes.api.commitImport(input.id), {
        method: 'POST',
      });
      return result.data;
    },
    // The proposals it produced are what a reviewer opens next, and they are read from the
    // queue's own query rather than from this reply.
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.proposals() }),
  });
}

export function useProduceExport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { format: string }) => {
      const result = await apiFetch<T.ExportReceipt>(routes.api.exports(), {
        method: 'POST',
        body: input,
      });
      return result.data;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.exports() }),
  });
}

export function useInviteUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: T.InviteUserRequest) => {
      const result = await apiFetch<T.InvitedUser>(routes.api.invitations(), {
        method: 'POST',
        body: input,
      });
      return result.data;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.users() });
    },
  });
}

/**
 * Suspend somebody, or let them back in.
 *
 * Suspension rather than deletion is the control that works for anybody who has done anything:
 * `active` is checked by every way in, while the work they recorded keeps naming them.
 */
export function useSetUserStanding() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; active: boolean }) => {
      const result = await apiFetch<T.User>(routes.api.user(input.id), {
        method: 'PATCH',
        body: { active: input.active },
      });
      return result.data;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.users() });
    },
  });
}

/** Only an account with no trace. The server refuses the rest with a sentence saying why. */
export function useDeleteUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await apiFetch<T.User>(routes.api.user(id), { method: 'DELETE' });
      return result.data;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.users() });
    },
  });
}

export function useDecidePendingChange(code: string, matter: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; decision: 'approve' | 'reject'; reason?: string }) => {
      const path =
        input.decision === 'approve'
          ? routes.api.approvePendingChange(code, matter, input.id)
          : routes.api.rejectPendingChange(code, matter, input.id);
      const result = await apiFetch<T.PendingChangeDecided>(path, {
        method: 'POST',
        body: input.reason ? { reason: input.reason } : {},
      });
      return result.data;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.pendingChanges(code, matter) });
      invalidateRegister(client, code);
    },
  });
}

export function useClearContradiction(code: string, matter: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { lane: string; versionId: string; reason: string }) => {
      const result = await apiFetch<T.RuleWrite>(
        routes.api.clearContradiction(code, matter, input.lane, input.versionId),
        { method: 'POST', body: { reason: input.reason } },
      );
      return result.data;
    },
    onSuccess: async () => {
      await invalidateRegister(client, code);
      await client.invalidateQueries({ queryKey: ['contradictions'] });
    },
  });
}

export function useAddSource(code: string, matter: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: T.AddSourceRequest) => {
      const result = await apiFetch<T.AddSourceResponse>(routes.api.sources(code, matter), {
        method: 'POST',
        body,
      });
      return result.data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.sources(code, matter) });
      await invalidateRegister(client, code);
    },
  });
}

export function useRemoveSource(code: string, matter: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (url: string) => {
      const result = await apiFetch<T.Sources>(
        `${routes.api.sources(code, matter)}?url=${encodeURIComponent(url)}`,
        { method: 'DELETE' },
      );
      return result.data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.sources(code, matter) });
      await invalidateRegister(client, code);
    },
  });
}

function invalidateQueue(client: QueryClient): Promise<void> {
  return Promise.all([
    client.invalidateQueries({ queryKey: ['queue'] }),
    client.invalidateQueries({ queryKey: ['jurisdictions'] }),
  ]).then(() => undefined);
}

export function useQueueResearch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: T.CreateQueueEntryRequest) => {
      const result = await apiFetch<T.CreateQueueEntryResponse>(routes.api.queue(), {
        method: 'POST',
        body,
      });
      return result.data;
    },
    onSuccess: () => invalidateQueue(client),
  });
}

export function useCancelQueueEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await apiFetch<T.QueueEntryResponse>(routes.api.queueCancel(id), {
        method: 'POST',
      });
      return result.data;
    },
    onSuccess: () => invalidateQueue(client),
  });
}

export function useRetryQueueEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await apiFetch<T.QueueEntryResponse>(routes.api.queueRetry(id), {
        method: 'POST',
      });
      return result.data;
    },
    onSuccess: () => invalidateQueue(client),
  });
}

export function useRemoveQueueEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await apiFetch<T.QueueDeleteResponse>(routes.api.queueEntry(id), {
        method: 'DELETE',
      });
      return result.data;
    },
    onSuccess: () => invalidateQueue(client),
  });
}

export function useConfirmProposal(id: string, code: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: T.ConfirmProposalRequest; etag: string }) => {
      const result = await apiFetch<T.ConfirmProposalResponse>(routes.api.confirmProposal(id), {
        method: 'POST',
        body: input.body,
        etag: input.etag,
      });
      return result.data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['proposals'] });
      await client.invalidateQueries({ queryKey: keys.proposal(id) });
      await invalidateRegister(client, code);
    },
  });
}

export function useDiscardProposal() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; etag: string; note: string }) => {
      const result = await apiFetch<T.DeleteProposalResponse>(routes.api.proposal(input.id), {
        method: 'DELETE',
        body: { note: input.note },
        etag: input.etag,
      });
      return result.data;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['proposals'] }),
  });
}

export function useSavePrompt(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: T.UpdatePromptRequest; etag: string }) => {
      const result = await apiFetch<T.UpdatePromptResponse>(routes.api.prompt(id), {
        method: 'PUT',
        body: input.body,
        etag: input.etag,
      });
      return result.data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.prompts });
      await client.invalidateQueries({ queryKey: keys.promptVersions(id) });
    },
  });
}
